import { useCallback, useMemo } from 'react'
import { OfficeAppIcon, type OfficeApp } from '@/components/landing/OfficeAppIcon'
import { StaggerContainer, StaggerItem, TextReveal } from '@/components/motion'
import {
  FilterBar,
  HalftoneCover,
  ListingCard,
  ListingEmpty,
  TONES,
  optionsFrom,
  useListingFilters,
  type FilterGroup,
  type ListingTag,
  type ListingTone,
} from '@/components/landing/listing'
import { type AIAgent, type IndustryData, type Template, type Workflow } from '@/data/industries'
import { cn } from '@/lib/utils'

/* ------------------------------------------------------------------------
   Shared listing kit for the industry layouts: generated covers, the
   listing items built from industries.ts, and the filterable listing
   section ("Templates and use cases for ...").
------------------------------------------------------------------------ */

export const APP_TONE: Record<Template['type'], ListingTone> = {
  Word: 'blue',
  Excel: 'green',
  PowerPoint: 'pink',
  PDF: 'black',
}

export const APP_ICON: Record<Template['type'], OfficeApp | null> = {
  Word: 'word',
  Excel: 'excel',
  PowerPoint: 'powerpoint',
  PDF: null,
}

type ItemKind = 'Template' | 'Use case' | 'AI agent'

interface ListingItem {
  id: string
  kind: ItemKind
  app: string | null
  agents: string[]
  title: string
  excerpt: string
  tag: ListingTag
  href: string
  cover: React.ReactNode
}

/** "30 minutes (vs. 90 minutes manual)" → "30 minutes". */
export function shortTime(timeSaved: string): string {
  const i = timeSaved.indexOf(' (')
  return i === -1 ? timeSaved : timeSaved.slice(0, i)
}

/** "30 minutes (vs. 90 minutes manual)" → "90 minutes manual", or null. */
export function manualTime(timeSaved: string): string | null {
  const match = /\(vs\.\s*([^)]+)\)/.exec(timeSaved)
  return match ? match[1]! : null
}

/** Template excerpt shared by every listing and grid. */
export function templateExcerpt(template: Template): string {
  return `${template.description}. ${template.type === 'Excel' ? 'An' : 'A'} ${template.type} template with AI-powered smart fields.`
}

/**
 * Lower-case the first letter (for joining data strings into a sentence),
 * leaving acronyms ("RFI received", "PHI exposure") alone.
 */
export const lowerFirst = (text: string) =>
  /^[A-Z]{2}/.test(text) ? text : `${text.charAt(0).toLowerCase()}${text.slice(1)}`

/** "a, b and c" from a list. */
export const joinList = (items: string[]) =>
  items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`

/** Split a heading's sentences ("Every A. Every B.") into lines. */
export function sentences(text: string): string[] {
  return text.split(/(?<=\.)\s+/).filter(Boolean)
}

/* ------------------------------------------------------------------------
   Generated covers (decorative, built from the item's own data)
------------------------------------------------------------------------ */

export function TemplateCover({ template, index }: { template: Template; index: number }) {
  const app = APP_ICON[template.type]
  return (
    <HalftoneCover tone={APP_TONE[template.type]} aspect="4 / 3" angle={[150, 60, 210, 20][index % 4]}>
      <div className="flex aspect-[3/4] h-[78%] flex-col gap-[6%] bg-white p-[6%] text-[#0a0a0a] shadow-[0_18px_30px_-16px_rgba(10,10,10,0.5)]">
        <div className="flex items-center justify-between">
          <span className="h-1.5 w-8" style={{ backgroundColor: TONES[APP_TONE[template.type]].bg }} />
          {app ? (
            <OfficeAppIcon app={app} className="h-6 w-6" />
          ) : (
            <span className="border border-[#0a0a0a]/30 px-1 text-[0.5rem] font-bold">PDF</span>
          )}
        </div>
        <span className="font-[family-name:var(--font-landing-subdisplay)] text-[0.6875rem] font-bold leading-tight">
          {template.name}
        </span>
        <span className="flex flex-col gap-1.5">
          {[92, 100, 74, 88, 60].map((w, i) => (
            <span key={i} className="block h-1 bg-[#0a0a0a]/12" style={{ width: `${w}%` }} />
          ))}
        </span>
      </div>
    </HalftoneCover>
  )
}

function WorkflowCover({ workflow, index }: { workflow: Workflow; index: number }) {
  const steps = workflow.steps.slice(0, 3)
  return (
    <HalftoneCover tone="purple" aspect="4 / 3" angle={[200, 300, 120, 30][index % 4]}>
      <ol className="flex w-full max-w-[17rem] flex-col">
        {steps.map((step, i) => (
          <li key={step} className="flex flex-col items-start">
            <span className="flex items-center gap-2 bg-white px-2.5 py-1.5 text-[0.6875rem] font-semibold leading-tight text-[#0a0a0a] shadow-[0_10px_20px_-12px_rgba(10,10,10,0.6)]">
              <span className="flex h-4 w-4 shrink-0 items-center justify-center bg-[#0a0a0a] text-[0.5625rem] text-white">
                {i + 1}
              </span>
              {step}
            </span>
            {i < steps.length - 1 && <span className="ml-[1.1rem] h-3 w-px bg-white" />}
          </li>
        ))}
      </ol>
    </HalftoneCover>
  )
}

const AGENT_TONES: ListingTone[] = ['blue', 'pink', 'black', 'green']

function AgentCover({ agent, index }: { agent: AIAgent; index: number }) {
  const tone = AGENT_TONES[index % AGENT_TONES.length]!
  const spec = TONES[tone]
  return (
    <HalftoneCover tone={tone} aspect="4 / 3" angle={100}>
      <div className="flex w-full max-w-[16rem] items-end gap-3">
        <span
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full font-[family-name:var(--font-landing-display)] text-base"
          style={{ backgroundColor: spec.fg, color: spec.bg }}
        >
          {agent.name[0]}
        </span>
        <span className="mb-3 bg-white px-3 py-2 text-xs font-semibold leading-snug text-[#0a0a0a] shadow-[0_12px_24px_-14px_rgba(10,10,10,0.6)]">
          {agent.catchphrase}
        </span>
      </div>
    </HalftoneCover>
  )
}

/* ------------------------------------------------------------------------
   Listing items from industries.ts
------------------------------------------------------------------------ */

function buildItems(industry: IndustryData): ListingItem[] {
  const agentNames = industry.aiAgents.map((a) => a.name)
  const templates: ListingItem[] = industry.templates.map((template, index) => ({
    id: `template-${template.name}`,
    kind: 'Template',
    app: template.type,
    agents: [],
    title: template.name,
    excerpt: templateExcerpt(template),
    tag: { label: `${template.type} template`, tone: 'blue' },
    href: '/platform/templates',
    cover: <TemplateCover template={template} index={index} />,
  }))
  const workflows: ListingItem[] = industry.workflows.map((workflow, index) => ({
    id: `workflow-${workflow.name}`,
    kind: 'Use case',
    app: null,
    agents: agentNames.filter((name) => workflow.steps.some((step) => step.startsWith(`${name} `))),
    title: workflow.name,
    excerpt: `${workflow.trigger}: ${lowerFirst(workflow.output)}, in ${workflow.timeSaved}.`,
    tag: { label: 'Use case', tone: 'purple' },
    href: '/platform/ai-generation',
    cover: <WorkflowCover workflow={workflow} index={index} />,
  }))
  const agents: ListingItem[] = industry.aiAgents.map((agent, index) => ({
    id: `agent-${agent.name}`,
    kind: 'AI agent',
    app: null,
    agents: [agent.name],
    title: `${agent.name}, ${agent.role}`,
    excerpt: agent.description,
    tag: { label: 'AI agent', tone: 'pink' },
    href: '/platform/ai-generation',
    cover: <AgentCover agent={agent} index={index} />,
  }))
  return [...templates, ...workflows, ...agents]
}

const itemValues = (item: ListingItem, groupId: string): string[] => {
  if (groupId === 'kind') return [item.kind]
  if (groupId === 'app') return item.app ? [item.app] : []
  if (groupId === 'agent') return item.agents
  return []
}

/**
 * The filterable listing (blogs reference): heading, filter bar and a
 * three-column grid of 4:3 cards covering the industry's templates, use
 * cases and agents. Renders the `#templates` anchor.
 */
export function IndustryListing({
  industry,
  heading,
  className,
}: {
  industry: IndustryData
  heading?: React.ReactNode
  className?: string
}) {
  const items = useMemo(() => buildItems(industry), [industry])
  const getValues = useCallback(itemValues, [])
  const { selection, toggle, clear, filtered } = useListingFilters(items, getValues)

  const groups: FilterGroup[] = useMemo(
    () => [
      { id: 'kind', label: 'Type', options: optionsFrom(items.map((i) => i.kind)) },
      { id: 'app', label: 'Office app', options: optionsFrom(industry.templates.map((t) => t.type)) },
      { id: 'agent', label: 'AI agent', options: optionsFrom(industry.aiAgents.map((a) => a.name)) },
    ],
    [items, industry]
  )

  return (
    <section id="templates" className={cn('landing-section scroll-mt-20', className)}>
      <div className="landing-container">
        <div className="mx-auto lg:max-w-[80%]">
          <TextReveal as="h2" className="landing-text-display-lg mb-10 max-w-[24ch] text-[var(--color-landing-gray-900)] dark:text-white">
            {heading ?? `Templates and use cases for ${industry.name.toLowerCase()}`}
          </TextReveal>
          <FilterBar
            groups={groups}
            selection={selection}
            onToggle={toggle}
            onClear={clear}
            resultLabel={`${filtered.length} of ${items.length} results`}
          />

          {filtered.length > 0 ? (
            <StaggerContainer amount={0.05} className="mt-12 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((item) => (
                <StaggerItem key={item.id}>
                  <ListingCard
                    href={item.href}
                    cover={item.cover}
                    title={item.title}
                    size="h6"
                    tags={[item.tag]}
                    tagsPosition="above"
                    excerpt={item.excerpt}
                    excerptLines={2}
                  />
                </StaggerItem>
              ))}
            </StaggerContainer>
          ) : (
            <ListingEmpty
              className="mt-12"
              title="Nothing matches these filters"
              description="Agents and use cases have no Office app of their own. Try another combination, or reset the filters."
              onReset={clear}
            />
          )}
        </div>
      </div>
    </section>
  )
}
